// BasicosTabXL - Sprint 44 Fase 3 Vista 3.
// Tab "Datos basicos": imagen, codigo, nombre, categoria (datalist),
// unidad de medida, estado, descripcion. La categoria reusa el patron
// <input list="..."> nativo (igual que legacy) dentro de un wrapper XL
// para mantener UX + autocompletado sin crear un componente nuevo.
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import XLInput from '../../../components/xl/XLInput';
import XLSelect from '../../../components/xl/XLSelect';
import ProductoImagen from '../components/ProductoImagen';

const CSS_ID = 'xl-prod-basicos-css';
const CSS = `
.xl-prod-body { display: flex; flex-direction: column; gap: 20px; font-family: var(--xl-font-sans) }

.xl-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
@media (max-width: 720px) { .xl-form-grid { grid-template-columns: 1fr } }
.xl-form-grid__full { grid-column: 1 / -1 }

.xl-prod-cat {
  display: flex; flex-direction: column; gap: 4px;
}
.xl-prod-cat__label {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary);
}
.xl-prod-cat__input {
  width: 100%;
  font: 400 14px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 9px 12px;
  box-sizing: border-box;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow 120ms cubic-bezier(0,0,0.2,1);
}
.xl-prod-cat__input:hover { border-color: rgba(46, 55, 69, 0.25) }
.xl-prod-cat__input:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-prod-cat__input:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-prod-cat__hint { font-size: 11px; color: var(--xl-color-text-muted) }

.xl-prod-desc {
  width: 100%;
  min-height: 96px;
  resize: vertical;
  font: 400 14px/1.5 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 10px 12px;
  box-sizing: border-box;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow 120ms cubic-bezier(0,0,0.2,1);
}
.xl-prod-desc:hover { border-color: rgba(46, 55, 69, 0.25) }
.xl-prod-desc:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-prod-desc:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-prod-desc-wrap { display: flex; flex-direction: column; gap: 4px }
.xl-prod-desc-wrap__label {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary);
}
`;

const DATALIST_ID = 'xl-prod-cat-options';
const ESTADO_OPTIONS = [
  { value: 'activo',   label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
];

export default function BasicosTabXL({
  form, set, errors = {},
  productoId, uploading, onUploadImage, onDeleteImage,
  categorias = [],
}) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const estado = form.estado || 'activo';

  return (
    <div className="xl-prod-body">
      <ProductoImagen
        productoId={productoId}
        imagenUrl={form.imagen_url}
        imagenThumbnailUrl={form.imagen_thumbnail_url}
        uploading={uploading}
        onUpload={onUploadImage}
        onDelete={onDeleteImage}
      />

      <div className="xl-form-grid">
        <XLInput
          label="Codigo *"
          value={form.codigo || ''}
          onChange={(e) => set('codigo', e.target.value)}
          error={errors.codigo}
          required
        />
        <XLSelect
          label="Estado"
          value={estado}
          onChange={(e) => set('estado', e.target.value)}
          options={ESTADO_OPTIONS}
        />
        <div className="xl-form-grid__full">
          <XLInput
            label="Nombre *"
            value={form.nombre || ''}
            onChange={(e) => set('nombre', e.target.value)}
            error={errors.nombre}
            required
          />
        </div>
        <div className="xl-prod-cat">
          <label className="xl-prod-cat__label" htmlFor="xl-prod-cat-input">
            Categoria
          </label>
          <input
            id="xl-prod-cat-input"
            className="xl-prod-cat__input"
            list={DATALIST_ID}
            type="text"
            value={form.categoria || ''}
            onChange={(e) => set('categoria', e.target.value)}
            placeholder="Escribe o elige..."
          />
          <datalist id={DATALIST_ID}>
            {categorias.map((c) => <option key={c} value={c} />)}
          </datalist>
          {errors.categoria && (
            <span className="xl-prod-cat__hint" style={{ color: 'var(--xl-color-danger)' }}>
              {errors.categoria}
            </span>
          )}
        </div>
        <XLInput
          label="Unidad de medida"
          value={form.unidad_medida || ''}
          onChange={(e) => set('unidad_medida', e.target.value)}
          placeholder="UND, KG, LT..."
          error={errors.unidad_medida}
        />
        <div className="xl-form-grid__full">
          <div className="xl-prod-desc-wrap">
            <label className="xl-prod-desc-wrap__label" htmlFor="xl-prod-desc">
              Descripcion
            </label>
            <textarea
              id="xl-prod-desc"
              className="xl-prod-desc"
              rows={3}
              value={form.descripcion || ''}
              onChange={(e) => set('descripcion', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
